import { create } from 'zustand';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
const TOKEN_KEY = 'rushprops.session.token';

export interface AccountProfile {
  email: string;
  created: number;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  age?: number | null;
  sex?: string | null;
  plan: 'FREE' | 'PREMIUM';
  premiumUntil?: number | null;
  isAdmin?: boolean;
  isLifetime?: boolean;
  hasProfilePhoto?: boolean;
  photoUpdatedAt?: number | null;
}

export interface AuthResult {
  token?: string;
  error?: string;
  verificationRequired?: boolean;
  emailVerificationRequired?: boolean;
  verified?: boolean;
  sent?: boolean;
  email?: string;
}

export class AuthRequestError extends Error {
  constructor(message: string, readonly status: number, readonly payload: Record<string, unknown> = {}) {
    super(message);
    this.name = 'AuthRequestError';
  }
}

async function request<T>(path: string, options: RequestInit = {}, token?: string | null): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const body = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    const message = typeof body.error === 'string' ? body.error : `Não foi possível concluir a solicitação (${response.status}).`;
    throw new AuthRequestError(message, response.status, body);
  }
  return body as T;
}

export class EmailVerificationRequired extends Error {
  constructor(readonly email: string) {
    super('Confirme seu e-mail para continuar.');
    this.name = 'EmailVerificationRequired';
  }
}

interface AuthState {
  token: string | null;
  profile: AccountProfile | null;
  signIn: (identifier: string, password: string) => Promise<void>;
  signUp: (details: { email: string; username: string; password: string; firstName: string; lastName: string; age: number; sex: string }) => Promise<AuthResult>;
  verifyEmail: (email: string, code: string) => Promise<AuthResult>;
  resendVerification: (email: string) => Promise<AuthResult>;
  refreshProfile: () => Promise<AccountProfile>;
  signOut: () => void;
  checkout: (plan: '1m' | '3m' | '6m') => Promise<string>;
  redeemKey: (code: string) => Promise<void>;
  uploadPhoto: (photo: Blob) => Promise<void>;
  profilePhotoUrl: () => string;
}

const readSavedToken = (): string | null => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  token: readSavedToken(),
  profile: null,

  signIn: async (identifier, password) => {
    let result: AuthResult;
    try {
      result = await request<AuthResult>('/auth/login', {
        method: 'POST', body: JSON.stringify({ email: identifier.trim(), password }),
      });
    } catch (error) {
      if (error instanceof AuthRequestError && error.payload.emailVerificationRequired === true) {
        throw new EmailVerificationRequired(String(error.payload.email || identifier.trim()));
      }
      throw error;
    }
    if (!result.token) throw new Error(result.error || 'Não foi possível entrar.');
    try {
      localStorage.setItem(TOKEN_KEY, result.token);
      set({ token: result.token });
    } catch {
      throw new Error('Não foi possível salvar a sessão neste aparelho.');
    }
    await get().refreshProfile();
  },

  signUp: async details => {
    const result = await request<AuthResult>('/auth/register', {
      method: 'POST', body: JSON.stringify(details),
    });
    if (result.token) {
      try {
        localStorage.setItem(TOKEN_KEY, result.token);
        set({ token: result.token });
      } catch {
        throw new Error('A conta foi criada, mas não foi possível salvar a sessão neste aparelho. Entre novamente.');
      }
      await get().refreshProfile();
    }
    return result;
  },

  verifyEmail: async (email, code) => request<AuthResult>('/auth/verify-email', {
    method: 'POST', body: JSON.stringify({ email: email.trim(), code: code.trim() }),
  }),

  resendVerification: async email => request<AuthResult>('/auth/resend-verification', {
    method: 'POST', body: JSON.stringify({ email: email.trim() }),
  }),

  refreshProfile: async () => {
    const token = get().token;
    if (!token) throw new Error('Entre na sua conta para continuar.');
    try {
      const profile = await request<AccountProfile>('/auth/me', {}, token);
      set({ profile });
      return profile;
    } catch (error) {
      if (error instanceof AuthRequestError && error.status === 401) get().signOut();
      throw error;
    }
  },

  signOut: () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage may be unavailable */ }
    set({ token: null, profile: null });
  },

  checkout: async plan => {
    const token = get().token;
    if (!token) throw new Error('Entre na sua conta antes de escolher o Premium.');
    const result = await request<{ url?: string; error?: string }>(`/billing/checkout?plan=${plan}`, {}, token);
    if (!result.url) throw new Error(result.error || 'O pagamento ainda não está disponível.');
    return result.url;
  },

  redeemKey: async code => {
    const token = get().token;
    if (!token) throw new Error('Entre na sua conta antes de resgatar uma chave.');
    await request('/trial-keys/redeem', { method: 'POST', body: JSON.stringify({ code }) }, token);
    await get().refreshProfile();
  },

  uploadPhoto: async photo => {
    const token = get().token;
    if (!token) throw new Error('Entre na sua conta para salvar uma foto de perfil.');
    await request('/auth/profile/photo', { method: 'POST', headers: { 'Content-Type': 'image/jpeg' }, body: photo }, token);
    await get().refreshProfile();
  },

  profilePhotoUrl: () => `${API_BASE}/auth/profile/photo`,
}));
